import React, { useState } from "react";
import { SSODomainsSheet } from "../../components/SSOForm/SSODomains/SSODomainsSheet";
import { useUserContext } from "../../../providers/UserProvider";
import { useOrganizationIdp } from "../../../hooks/useOrganizationIdp/useOrganizationIdp";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { Attention, Typography } from "@optimizely/axiom";
import { IdentityProviderDropdown } from "../../components/SSOForm/SSODomains/IdentityProviderDropdown";
import { IdentityProvider } from "../../../domain/IdentityProvider";
import { DomainsDisclosure } from "../../components/SSOForm/SSODomains/DomainsDisclosure";

export const DomainsPage = () => {
    const { organizationId } = useUserContext();
    // The hook owns the SCIM-exclusion rule and exposes ready-to-render IdP groupings:
    // - idpsWithDomains: connections with existing domains (shown under "Existing Domains")
    // - idpsEligibleForDomainAdd: non-SCIM connections without domains (offered in the dropdown)
    // - scimManagedIdpsWithoutDomains: SCIM connections excluded from the dropdown (explained below)
    const {
        identityProviders,
        idpsWithDomains,
        idpsEligibleForDomainAdd,
        scimManagedIdpsWithoutDomains,
        updateIdpDomains,
        isLoadingIdp
    } = useOrganizationIdp(organizationId);
    const [domainsIdp, setDomainsIdp] = useState<IdentityProvider | null>(null);

    if (isLoadingIdp) return <LoadingIndicator height="100%" type="spinner" />;

    const isSingleScimIdp = scimManagedIdpsWithoutDomains.length === 1;

    return (
        <div className="flex flex--column flex-align--start push-double content-container">
            <>
                <Typography type="header1">Domains / Dynamic Provisioning</Typography>
                <Typography tag="p" type="caption">
                    Specify an optional list of domains for each SSO connection for your organization.
                </Typography>
                <Typography tag="p" type="caption">
                    Adding one or more domains for your organization allows users from your organization matching one of
                    the domains to be provisioned dynamically to OptiId.
                </Typography>
                <IdentityProviderDropdown
                    identityProviders={idpsEligibleForDomainAdd}
                    onChange={({ identityProvider }) => setDomainsIdp(identityProvider)}
                />
                {!!scimManagedIdpsWithoutDomains.length && (
                    <Attention
                        type="warning"
                        alignment="left"
                        className="push-double--top"
                        data-testid="scim-domains-excluded-note"
                    >
                        {isSingleScimIdp ? "The connection " : "The following connections "}
                        <span className="weight--bold">
                            {scimManagedIdpsWithoutDomains.map((idp) => idp.idpName).join(", ")}
                        </span>
                        {isSingleScimIdp ? " uses" : " use"} SCIM provisioning, so {isSingleScimIdp ? "its" : "their"}{" "}
                        domains are managed by SCIM and can&apos;t be added or edited here.
                    </Attention>
                )}
                {!identityProviders?.length && (
                    <Typography type="caption" className="push--top">
                        SSO needs to be connected before adding SSO Domains.
                    </Typography>
                )}
                {!!idpsWithDomains.length && (
                    <Typography type="header4" className="push-double--ends">
                        Existing Domains
                    </Typography>
                )}
                {idpsWithDomains.map((idp) => {
                    return <DomainsDisclosure identityProvider={idp} onEdit={() => setDomainsIdp(idp)} key={idp.id} />;
                })}
                {!!domainsIdp && (
                    <SSODomainsSheet
                        identityProvider={domainsIdp}
                        onClose={() => setDomainsIdp(null)}
                        onSave={async ({ domains }) => {
                            return await updateIdpDomains({ domains, identityProviderId: domainsIdp.id });
                        }}
                    />
                )}
            </>
        </div>
    );
};

DomainsPage.displayName = "DomainsPage";
